/**
 * Decoration routing helpers (PR-L3).
 *
 * Companion to api/src/endpoints/decoration_variants.py's
 * variant_for_color resolver: classifies each color on a product against
 * the variant list on a given decoration, using the same resolution
 * order as the backend. Surfaces a boolean "fully routed" verdict + the
 * list of uncovered SKUs for the ReadinessBanner.
 *
 * Resolution order (mirrors PR-J's resolver):
 *   1. specific_colors variant whose color_skus contains the SKU.
 *   2. catch_all_light variant when the SKU classifies as light.
 *   3. catch_all_dark variant when the SKU classifies as dark.
 *   4. single_catch_all variant.
 *   5. uncovered.
 *
 * Pure functions. No store reads, no I/O, no input mutation. Safe to
 * call from any setup() / computed() / template binding / spec test.
 *
 * Light/Dark classification uses the same classifyLDWithName pipeline
 * the frontend uses elsewhere (DecorationInspector previewingVariantKey,
 * variantStrategy.js suggestion). That keeps the verdict consistent
 * with what the user sees in ColorFilmstrip pips. The backend resolver
 * uses classify_ld (hex-only) + manual_ld override; for the frontend
 * readiness check we only have what the editor store carries, which is
 * the supplierMeta colors + per-product manual_ld override map (if
 * any). The caller passes productColors with name/hex already resolved.
 */
import { classifyLDWithName } from '@/utils/luminance';

export const VARIANT_KIND_SINGLE_CATCH_ALL = 'single_catch_all';
export const VARIANT_KIND_CATCH_ALL_LIGHT = 'catch_all_light';
export const VARIANT_KIND_CATCH_ALL_DARK = 'catch_all_dark';
export const VARIANT_KIND_SPECIFIC_COLORS = 'specific_colors';

/**
 * @typedef {Object} ColorInput
 * @property {string} color_sku
 * @property {string|null} [name]
 * @property {string|null} [hex]
 */

/**
 * @typedef {Object} VariantInput
 * @property {string} kind  One of the VARIANT_KIND_* constants.
 * @property {string[]} [color_skus]  Required for specific_colors.
 */

function ldOf(color) {
  if (!color || typeof color !== 'object') return null;
  const { ld } = classifyLDWithName({
    hex: color.hex ?? null,
    name: color.name ?? null,
  });
  return ld === 'light' || ld === 'dark' ? ld : null;
}

/**
 * Classify each color in the product's selected_color_skus against the
 * decoration's variants. Returns a Map<color_sku, kind|null>, where
 * `null` means uncovered.
 *
 * @param {{ variants?: VariantInput[] }} decoration
 * @param {ColorInput[]} productColors
 * @returns {Map<string, string|null>}
 */
export function classifyColorsByVariant(decoration, productColors) {
  const out = new Map();
  if (!Array.isArray(productColors) || productColors.length === 0) return out;

  const variants = (decoration && Array.isArray(decoration.variants))
    ? decoration.variants
    : [];

  // Bucket variants by kind for O(1) lookup. specific_colors uses a
  // SKU-> winning-variant index so the override check is also O(1).
  const specificBySku = new Map();
  let hasLight = false;
  let hasDark = false;
  let hasSingle = false;
  for (const v of variants) {
    if (!v || typeof v !== 'object') continue;
    if (v.kind === VARIANT_KIND_SPECIFIC_COLORS) {
      const list = Array.isArray(v.color_skus) ? v.color_skus : [];
      for (const sku of list) {
        if (typeof sku === 'string' && !specificBySku.has(sku)) {
          specificBySku.set(sku, v);
        }
      }
    } else if (v.kind === VARIANT_KIND_CATCH_ALL_LIGHT) {
      hasLight = true;
    } else if (v.kind === VARIANT_KIND_CATCH_ALL_DARK) {
      hasDark = true;
    } else if (v.kind === VARIANT_KIND_SINGLE_CATCH_ALL) {
      hasSingle = true;
    }
  }

  for (const color of productColors) {
    if (!color || typeof color.color_sku !== 'string') continue;
    const sku = color.color_sku;

    // 1) specific_colors wins.
    if (specificBySku.has(sku)) {
      out.set(sku, VARIANT_KIND_SPECIFIC_COLORS);
      continue;
    }

    // 2 + 3) catch_all_light / catch_all_dark, gated on L/D verdict.
    if (hasLight || hasDark) {
      const ld = ldOf(color);
      if (ld === 'light' && hasLight) {
        out.set(sku, VARIANT_KIND_CATCH_ALL_LIGHT);
        continue;
      }
      if (ld === 'dark' && hasDark) {
        out.set(sku, VARIANT_KIND_CATCH_ALL_DARK);
        continue;
      }
    }

    // 4) single_catch_all when no specific/light/dark route matched.
    if (hasSingle) {
      out.set(sku, VARIANT_KIND_SINGLE_CATCH_ALL);
      continue;
    }

    // 5) Uncovered.
    out.set(sku, null);
  }
  return out;
}

/**
 * Return the list of color SKUs on the product that no variant on the
 * decoration routes, in input order. Empty when fully routed.
 *
 * @param {{ variants?: VariantInput[] }} decoration
 * @param {ColorInput[]} productColors
 * @returns {string[]}
 */
export function uncoveredColorSkus(decoration, productColors) {
  if (!Array.isArray(productColors)) return [];
  const verdict = classifyColorsByVariant(decoration, productColors);
  const out = [];
  for (const color of productColors) {
    if (!color || typeof color.color_sku !== 'string') continue;
    if (verdict.get(color.color_sku) == null) out.push(color.color_sku);
  }
  return out;
}

/**
 * True when every product color is routed by some variant on the
 * decoration. Vacuously true when the product has no colors. False when
 * the decoration has no variants and the product has at least one
 * color.
 *
 * @param {{ variants?: VariantInput[] }} decoration
 * @param {ColorInput[]} productColors
 * @returns {boolean}
 */
export function isDecorationFullyRouted(decoration, productColors) {
  return uncoveredColorSkus(decoration, productColors).length === 0;
}

/**
 * Count product colors that classify as light (per classifyLDWithName).
 * Helper for VariantCard's catch_all_light subline.
 *
 * @param {ColorInput[]} productColors
 * @returns {number}
 */
export function countLightColors(productColors) {
  if (!Array.isArray(productColors)) return 0;
  let n = 0;
  for (const c of productColors) {
    if (ldOf(c) === 'light') n += 1;
  }
  return n;
}

/**
 * Count product colors that classify as dark.
 *
 * @param {ColorInput[]} productColors
 * @returns {number}
 */
export function countDarkColors(productColors) {
  if (!Array.isArray(productColors)) return 0;
  let n = 0;
  for (const c of productColors) {
    if (ldOf(c) === 'dark') n += 1;
  }
  return n;
}
